import React, { useState, useCallback } from 'react'
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
} from 'react-native'
import { CheckCircle, Send } from 'lucide-react-native'
import axios from 'axios'
import { buildEndpointUrl } from '../../constants/api'
import { getApiErrorMessage } from '../../src/shared/helpers/deepLink.helper'
import { useSnackbar } from '../../src/shared/hooks/useSnackbar'
import { contactFormStyles, contactFormUiTokens } from './ContactForm.styles'
import {
  MAX_NAME_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_MESSAGE_LENGTH,
  sanitizeInput,
  sanitizeEmailInput,
} from '../../src/helpers/sanitizeInput.helper'
import { validateName, validateEmail, validateMessage } from './contactForm.helper'
import { useCaptchaToken } from '../../src/auth/turnstile/useCaptchaToken'

interface ContactFormProps {
  translations: {
    nameLabel: string
    emailLabel: string
    messageLabel: string
    submitButton: string
    submitting: string
    successMessage: string
    errorMessage: string
    namePlaceholder: string
    emailPlaceholder: string
    messagePlaceholder: string
    validation: {
      nameRequired: string
      nameTooLong: string
      emailRequired: string
      emailInvalid: string
      messageRequired: string
      messageTooLong: string
      invalidCharacters: string
    }
  }
  /** Test ID for testing */
  testID?: string
}

export const ContactForm: React.FC<ContactFormProps> = ({ translations, testID }) => {
  const { showSnackbar } = useSnackbar()
  const { getCaptchaToken, resetCaptcha, CaptchaWidget } = useCaptchaToken()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  // Validation errors (shown below fields)
  const [nameError, setNameError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [messageError, setMessageError] = useState<string | null>(null)

  // Track if fields have been touched (to show errors only after interaction)
  const [nameTouched, setNameTouched] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)
  const [messageTouched, setMessageTouched] = useState(false)

  // ============================================================================
  // Input Change Handlers with Real-time Sanitization
  // ============================================================================

  /**
   * Handle name input changes
   *
   * SECURITY: Immediately sanitizes input to prevent dangerous characters
   * - Removes < and > characters (prevents HTML injection)
   * - Removes script tags and dangerous patterns
   * - Limits length to MAX_NAME_LENGTH
   * - Validates in real-time if field has been touched
   */
  const handleNameChange = useCallback(
    (text: string) => {
      // CRITICAL: Sanitize immediately - removes <, >, and other dangerous chars
      // allowSpaces: keep trailing/leading spaces while typing; trim on submit (defense unchanged)
      const sanitized = sanitizeInput(text, true)

      // Limit length client-side to prevent backend rejection
      const limited = sanitized.slice(0, MAX_NAME_LENGTH)
      setName(limited)

      // Real-time validation (only show errors after user has interacted with field)
      if (nameTouched) {
        const validation = validateName(limited)
        setNameError(
          validation.error
            ? translations.validation[
                validation.error as keyof typeof translations.validation
              ]
            : null
        )
      }
    },
    [nameTouched, translations]
  )

  /**
   * Handle email input changes
   *
   * SECURITY: Removes dangerous characters while preserving valid email format
   * - Allows @ symbol (required for email format)
   * - Blocks all other dangerous characters: <, >, ', ", `, ;, \, /, |, &, $, ^, *, +, =, (, ), {, }, [, ], --, #, %
   * - Removes control characters (null bytes, etc.)
   * - Limits length to MAX_EMAIL_LENGTH
   * - Validates in real-time if field has been touched
   */
  const handleEmailChange = useCallback(
    (text: string) => {
      // SECURITY: Sanitize email input (allows @ but blocks all other dangerous chars)
      const sanitized = sanitizeEmailInput(text)

      // Limit length client-side
      const limited = sanitized.slice(0, MAX_EMAIL_LENGTH)
      setEmail(limited)

      // Real-time validation (only show errors after user has interacted with field)
      if (emailTouched) {
        const validation = validateEmail(limited)
        setEmailError(
          validation.error
            ? translations.validation[
                validation.error as keyof typeof translations.validation
              ]
            : null
        )
      }
    },
    [emailTouched, translations]
  )

  /**
   * Handle message input changes
   *
   * SECURITY: Immediately sanitizes input to prevent dangerous characters
   * - Removes < and > characters (prevents HTML injection)
   * - Removes script tags and dangerous patterns
   * - Allows newlines and tabs for message formatting
   * - Limits length to MAX_MESSAGE_LENGTH
   * - Validates in real-time if field has been touched
   */
  const handleMessageChange = useCallback(
    (text: string) => {
      // CRITICAL: Sanitize immediately - removes <, >, and other dangerous chars
      const sanitized = sanitizeInput(text, true)

      // Limit length client-side to prevent backend rejection
      const limited = sanitized.slice(0, MAX_MESSAGE_LENGTH)
      setMessage(limited)

      // Real-time validation (only show errors after user has interacted with field)
      if (messageTouched) {
        const validation = validateMessage(limited)
        setMessageError(
          validation.error
            ? translations.validation[
                validation.error as keyof typeof translations.validation
              ]
            : null
        )
      }
    },
    [messageTouched, translations]
  )

  // ============================================================================
  // Form Submission Handler
  // ============================================================================

  /**
   * Handle form submission
   *
   * SECURITY: Multi-layer validation and sanitization
   * 1. Marks all fields as touched to show validation errors
   * 2. Validates each field using validation functions
   * 3. Prevents submission if any field is invalid
   * 4. Sanitizes data one more time before sending (defense in depth)
   *
   * This ensures no dangerous characters (<, >, script tags) reach the backend
   */
  const handleSubmit = async () => {
    // Mark all fields as touched to show validation errors
    setNameTouched(true)
    setEmailTouched(true)
    setMessageTouched(true)

    // SECURITY: Validate all fields before submission
    // This is a second layer of validation (first layer is in onChange handlers)
    const nameValidation = validateName(name)
    const emailValidation = validateEmail(email)
    const messageValidation = validateMessage(message)

    // Display validation errors to user
    setNameError(
      nameValidation.error
        ? translations.validation[
            nameValidation.error as keyof typeof translations.validation
          ]
        : null
    )
    setEmailError(
      emailValidation.error
        ? translations.validation[
            emailValidation.error as keyof typeof translations.validation
          ]
        : null
    )
    setMessageError(
      messageValidation.error
        ? translations.validation[
            messageValidation.error as keyof typeof translations.validation
          ]
        : null
    )

    // SECURITY: Block submission if any field is invalid
    // This prevents invalid data from reaching the backend
    if (
      !nameValidation.isValid ||
      !emailValidation.isValid ||
      !messageValidation.isValid
    ) {
      return
    }

    setIsSubmitting(true)
    setSubmitSuccess(false)

    try {
      // SECURITY: Sanitize data one more time before sending (defense in depth)
      // Even though we sanitize on input, this ensures no dangerous data reaches backend
      // This protects against any edge cases or if sanitization was bypassed
      const sanitizedName = sanitizeInput(name.trim())
      const sanitizedEmail = sanitizeEmailInput(email.trim()) // Sanitize email (allows @ but blocks dangerous chars)
      const sanitizedMessage = sanitizeInput(message.trim())

      const captchaToken = await getCaptchaToken()

      const url = buildEndpointUrl('contact', 'submit')
      const response = await axios.post(
        url,
        {
          name: sanitizedName,
          email: sanitizedEmail,
          message: sanitizedMessage,
          turnstileToken: captchaToken,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      resetCaptcha()

      const data = response.data

      if (!data.success) {
        throw new Error(data.error || translations.errorMessage)
      }

      if (data.success) {
        setSubmitSuccess(true)
        setName('')
        setEmail('')
        setMessage('')
        showSnackbar(translations.successMessage, 'success')
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false)
        }, 5000)
      } else {
        throw new Error(data.error || translations.errorMessage)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Contact form submission error:', error)

      resetCaptcha()
      let userMessage = translations.errorMessage
      const isAxiosErrorInstance =
        typeof error === 'object'
        && error !== null
        && 'isAxiosError' in error
        && (error as { isAxiosError?: boolean }).isAxiosError === true

      if (isAxiosErrorInstance) {
        userMessage = getApiErrorMessage(error, translations.errorMessage)
      } else if (error instanceof Error) {
        userMessage = error.message
      }

      showSnackbar(userMessage, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return (
      <View style={contactFormStyles.successContainer} testID={testID ? `${testID}-success` : 'contact-form-success'}>
        <CheckCircle size={48} color={contactFormUiTokens.successIconColor} />
        <Text style={contactFormStyles.successText}>
          {translations.successMessage}
        </Text>
      </View>
    )
  }

  return (
    <View style={contactFormStyles.formContainer} testID={testID || 'contact-form'}>
      <View style={contactFormStyles.inputGroup}>
        <Text style={contactFormStyles.label}>{translations.nameLabel}</Text>
        <TextInput
          style={[
            contactFormStyles.input,
            nameError && contactFormStyles.inputError,
          ]}
          value={name}
          onChangeText={handleNameChange}
          onBlur={() => {
            setNameTouched(true)
            const validation = validateName(name)
            setNameError(
              validation.error
                ? translations.validation[
                    validation.error as keyof typeof translations.validation
                  ]
                : null
            )
          }}
          placeholder={translations.namePlaceholder}
          placeholderTextColor={contactFormUiTokens.placeholderTextColor}
          maxLength={MAX_NAME_LENGTH}
          editable={!isSubmitting}
          testID={testID ? `${testID}-name-input` : 'contact-form-name-input'}
        />
        {nameError ? (
          <View style={contactFormStyles.errorContainer}>
            <Text style={contactFormStyles.errorText}>{nameError}</Text>
          </View>
        ) : null}
        <Text style={contactFormStyles.characterCount}>
          {name.length}/{MAX_NAME_LENGTH}
        </Text>
      </View>

      <View style={contactFormStyles.inputGroup}>
        <Text style={contactFormStyles.label}>{translations.emailLabel}</Text>
        <TextInput
          style={[
            contactFormStyles.input,
            emailError && contactFormStyles.inputError,
          ]}
          value={email}
          onChangeText={handleEmailChange}
          onBlur={() => {
            setEmailTouched(true)
            const validation = validateEmail(email)
            setEmailError(
              validation.error
                ? translations.validation[
                    validation.error as keyof typeof translations.validation
                  ]
                : null
            )
          }}
          placeholder={translations.emailPlaceholder}
          placeholderTextColor={contactFormUiTokens.placeholderTextColor}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          maxLength={MAX_EMAIL_LENGTH}
          editable={!isSubmitting}
          testID={testID ? `${testID}-email-input` : 'contact-form-email-input'}
        />
        {emailError ? (
          <View style={contactFormStyles.errorContainer}>
            <Text style={contactFormStyles.errorText}>{emailError}</Text>
          </View>
        ) : null}
      </View>

      <View style={contactFormStyles.inputGroup}>
        <Text style={contactFormStyles.label}>{translations.messageLabel}</Text>
        <TextInput
          style={[
            contactFormStyles.input,
            contactFormStyles.textArea,
            messageError && contactFormStyles.inputError,
          ]}
          value={message}
          onChangeText={handleMessageChange}
          onBlur={() => {
            setMessageTouched(true)
            const validation = validateMessage(message)
            setMessageError(
              validation.error
                ? translations.validation[
                    validation.error as keyof typeof translations.validation
                  ]
                : null
            )
          }}
          placeholder={translations.messagePlaceholder}
          placeholderTextColor={contactFormUiTokens.placeholderTextColor}
          multiline
          numberOfLines={6}
          textAlignVertical="top"
          maxLength={MAX_MESSAGE_LENGTH}
          editable={!isSubmitting}
          testID={testID ? `${testID}-message-input` : 'contact-form-message-input'}
        />
        {messageError ? (
          <View style={contactFormStyles.errorContainer}>
            <Text style={contactFormStyles.errorText}>{messageError}</Text>
          </View>
        ) : null}
        <Text style={contactFormStyles.characterCount}>
          {message.length}/{MAX_MESSAGE_LENGTH}
        </Text>
      </View>

      {CaptchaWidget}

      <Pressable
        style={[
          contactFormStyles.submitButton,
          isSubmitting && contactFormStyles.submitButtonDisabled,
        ]}
        onPress={handleSubmit}
        disabled={isSubmitting}
        testID={testID ? `${testID}-submit-button` : 'contact-form-submit-button'}
      >
        {isSubmitting ? (
          <>
            <ActivityIndicator
              size="small"
              color={contactFormUiTokens.submitIconColor}
              style={contactFormStyles.submitButtonLoader}
            />
            <Text style={contactFormStyles.submitButtonText}>
              {translations.submitting}
            </Text>
          </>
        ) : (
          <>
            <Send
              size={20}
              color={contactFormUiTokens.submitIconColor}
              style={contactFormStyles.submitButtonIcon}
            />
            <Text style={contactFormStyles.submitButtonText}>
              {translations.submitButton}
            </Text>
          </>
        )}
      </Pressable>
    </View>
  )
}
